import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { Order, Note } from '../types';
import { StatusBadge } from './StatusBadge';
export interface OrdersTableProps {
  orders: Order[];
  notes?: Note[];
}
type SortField = 'createdAt' | 'loanNumber' | 'borrowerName' | 'orderType' | 'status' | 'assignedTo';
type SortDirection = 'asc' | 'desc';
export function OrdersTable({
  orders,
  notes = []
}: OrdersTableProps) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const sortedOrders = [...orders].sort((a, b) => {
    let aVal: any = a[sortField];
    let bVal: any = b[sortField];
    if (sortField === 'createdAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatOrderType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };
  const getLatestNote = (orderId: string) => {
    const orderNotes = notes.filter(n => n.orderId === orderId).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return orderNotes[0] || null;
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4" data-id="element-282" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-283" />;
  };
  if (orders.length === 0) {
    return <div className="bg-white border border-slate-100 rounded-2xl p-16 text-center" data-id="element-284">
        <p className="text-slate-600 mb-2 text-lg" data-id="element-285">No orders found</p>
        <p className="text-sm text-slate-400" data-id="element-286">
          Try adjusting your filters or create a new order
        </p>
      </div>;
  }
  return <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-id="element-287">
      <div className="overflow-x-auto" data-id="element-288">
        <table className="w-full" data-id="element-289">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-290">
            <tr data-id="element-291">
              <th onClick={() => handleSort('loanNumber')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-292">
                <div className="flex items-center space-x-1" data-id="element-293">
                  <span data-id="element-294">Loan #</span>
                  <SortIcon field="loanNumber" data-id="element-295" />
                </div>
              </th>
              <th onClick={() => handleSort('borrowerName')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-296">
                <div className="flex items-center space-x-1" data-id="element-297">
                  <span data-id="element-298">Borrower</span>
                  <SortIcon field="borrowerName" data-id="element-299" />
                </div>
              </th>
              <th onClick={() => handleSort('orderType')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-300">
                <div className="flex items-center space-x-1" data-id="element-301">
                  <span data-id="element-302">Type</span>
                  <SortIcon field="orderType" data-id="element-303" />
                </div>
              </th>
              <th onClick={() => handleSort('status')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-304">
                <div className="flex items-center space-x-1" data-id="element-305">
                  <span data-id="element-306">Status</span>
                  <SortIcon field="status" data-id="element-307" />
                </div>
              </th>
              <th onClick={() => handleSort('assignedTo')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-308">
                <div className="flex items-center space-x-1" data-id="element-309">
                  <span data-id="element-310">Assigned To</span>
                  <SortIcon field="assignedTo" data-id="element-311" />
                </div>
              </th>
              <th onClick={() => handleSort('createdAt')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-312">
                <div className="flex items-center space-x-1" data-id="element-313">
                  <span data-id="element-314">Created</span>
                  <SortIcon field="createdAt" data-id="element-315" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-316">
                <span data-id="element-317">Recent Note</span>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-318">
                <span data-id="element-319">Note Date</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200" data-id="element-320">
            {sortedOrders.map(order => <tr key={order.id} onClick={() => navigate(`/orders/${order.id}`)} className="cursor-pointer transition-colors" style={{
            ':hover': {
              backgroundColor: '#FEF2F2'
            }
          }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FEF2F2'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'} data-id="element-321">
                <td className="px-6 py-3 whitespace-nowrap text-sm font-semibold text-slate-900" data-id="element-322">
                  {order.loanNumber}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-700" data-id="element-323">
                  {order.borrowerName}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-600" data-id="element-324">
                  {formatOrderType(order.orderType)}
                </td>
                <td className="px-6 py-3 whitespace-nowrap" data-id="element-325">
                  <StatusBadge status={order.status} data-id="element-326" />
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-600" data-id="element-327">
                  {order.assignedTo}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-400" data-id="element-328">
                  {formatDate(order.createdAt)}
                </td>
                <td className="px-6 py-3 text-sm text-slate-600 max-w-[220px] truncate" data-id="element-329">
                  {getLatestNote(order.id)?.content || <span className="text-slate-300 italic" data-id="element-330">No notes</span>}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-400" data-id="element-331">
                  {getLatestNote(order.id) ? formatDate(getLatestNote(order.id)!.createdAt) : '—'}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}